import React from 'react';
import {
  Button,
  CircularProgress,
  DialogContent,
} from '@mui/material';
import { Inventory as InventoryIcon } from '@mui/icons-material';
import AppDialog, { AppDialogFormActions, AppDialogHeader } from '../common/AppDialog';
import { Item, CreateItemRequest, UpdateItemRequest, PriceEntryMode } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import ItemForm from './ItemForm';

interface ItemDialogProps {
  open: boolean;
  item?: Item | null;
  onClose: () => void;
  onSubmit: (data: CreateItemRequest | UpdateItemRequest) => Promise<void>;
  priceEntryMode?: PriceEntryMode;
  defaultVatRate?: number;
  batchTrackingEnabled?: boolean;
  isLoading?: boolean;
}

const ItemDialog: React.FC<ItemDialogProps> = ({
  open,
  item,
  onClose,
  onSubmit,
  priceEntryMode,
  defaultVatRate,
  batchTrackingEnabled,
  isLoading = false,
}) => {
  const { t } = useLanguage();

  const handleSubmit = async (data: CreateItemRequest | UpdateItemRequest) => {
    await onSubmit(data);
    onClose();
  };

  return (
    <AppDialog presentation="form" closeDisabled={isLoading}
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <AppDialogHeader icon={<InventoryIcon />} subtitle={item?.name}>
        {item ? t('items.editItem') : t('items.addNewItem')}
      </AppDialogHeader>
      
      <DialogContent 
        sx={{ flex: 1 }}
      >
        <ItemForm
          item={item}
          onSubmit={handleSubmit}
          onCancel={onClose}
          priceEntryMode={priceEntryMode}
          defaultVatRate={defaultVatRate}
          batchTrackingEnabled={batchTrackingEnabled}
          isLoading={isLoading}
          formId="item-dialog-form"
          showActions={false}
        />
      </DialogContent>
      <AppDialogFormActions edgeToEdge={false}>
        <Button onClick={onClose} disabled={isLoading} color="inherit">{t('common.cancel')}</Button>
        <Button
          data-testid="item-submit"
          type="submit"
          form="item-dialog-form"
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? t('items.saving') : item ? t('items.update') : t('items.create')}
        </Button>
      </AppDialogFormActions>
    </AppDialog>
  );
};

export default ItemDialog;
